import { ensureSharedTableStyles } from './dataTableStyles';

const FROZEN_COLUMN_ATTRIBUTE = 'data-datacopro-frozen-column';
const FROZEN_COLUMN_EDGE_ATTRIBUTE = 'data-datacopro-frozen-column-edge';
const FROZEN_COLUMN_LEFT_VARIABLE = '--datacopro-frozen-column-left';
const FROZEN_COLUMN_Z_INDEX_VARIABLE = '--datacopro-frozen-column-z-index';
const FROZEN_COLUMN_BACKGROUND_VARIABLE = '--datacopro-frozen-column-background';

const isVisible = (element) => window.getComputedStyle(element).display !== 'none';

const isReactDataTable = (table) => table.classList.contains('rdt_Table');

const getRows = (table) => {
    if (isReactDataTable(table)) {
        return Array.from(table.querySelectorAll('.rdt_TableHeadRow, .rdt_TableRow'));
    }

    return Array.from(table.querySelectorAll('thead tr, tbody tr, tfoot tr'));
};

const getCells = (row) => Array.from(row.children).filter(isVisible);

const getFrozenColumnCount = (table) => (
    table.closest('.qvalue-summary-table') ? 3 : 2
);

const addQValueTooltips = (table) => {
    if (!table.closest('.qvalue-summary-table')) return;

    table.querySelectorAll('.rdt_TableRow .rdt_TableCell').forEach((cell) => {
        const text = cell.textContent.trim();
        if (text) cell.setAttribute('title', text);
    });
};

const clearFrozenColumns = (table) => {
    table.querySelectorAll(`[${FROZEN_COLUMN_ATTRIBUTE}]`).forEach((cell) => {
        cell.removeAttribute(FROZEN_COLUMN_ATTRIBUTE);
        cell.removeAttribute(FROZEN_COLUMN_EDGE_ATTRIBUTE);
        cell.style.removeProperty(FROZEN_COLUMN_LEFT_VARIABLE);
        cell.style.removeProperty(FROZEN_COLUMN_Z_INDEX_VARIABLE);
        cell.style.removeProperty(FROZEN_COLUMN_BACKGROUND_VARIABLE);
    });
};

const setFrozenColumns = (table, frozenColumnCount) => {
    clearFrozenColumns(table);

    getRows(table).forEach((row) => {
        let leftOffset = 0;
        const cells = getCells(row);

        cells.slice(0, frozenColumnCount).forEach((cell, index) => {
            const backgroundColor = window.getComputedStyle(cell).backgroundColor;
            const rowBackgroundColor = window.getComputedStyle(row).backgroundColor;

            cell.setAttribute(FROZEN_COLUMN_ATTRIBUTE, String(index + 1));
            cell.style.setProperty(FROZEN_COLUMN_LEFT_VARIABLE, `${leftOffset}px`);
            cell.style.setProperty(FROZEN_COLUMN_Z_INDEX_VARIABLE, String(20 + frozenColumnCount - index));
            cell.style.setProperty(
                FROZEN_COLUMN_BACKGROUND_VARIABLE,
                backgroundColor === 'rgba(0, 0, 0, 0)' ? rowBackgroundColor : backgroundColor
            );

            if (index === frozenColumnCount - 1) {
                cell.setAttribute(FROZEN_COLUMN_EDGE_ATTRIBUTE, 'true');
            }

            leftOffset += cell.getBoundingClientRect().width;
        });
    });
};

const getSupportedTables = () => Array.from(
    document.querySelectorAll('.rdt_Table, table.table')
).filter((table) => !table.closest('.inventory-status-overview-table-wrap'));

/**
 * Applies measured sticky offsets to react-data-table-component and Bootstrap tables.
 * QValue summary tables opt into three frozen columns through their wrapper class.
 */
export function startDataTableFreezeManager() {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return () => {};
    }

    ensureSharedTableStyles();

    const resizeObserver = new ResizeObserver(scheduleRefresh);
    const observedTables = new Set();
    let refreshFrame = null;

    function refresh() {
        refreshFrame = null;

        getSupportedTables().forEach((table) => {
            if (!observedTables.has(table)) {
                resizeObserver.observe(table);
                observedTables.add(table);
            }

            setFrozenColumns(table, getFrozenColumnCount(table));
            addQValueTooltips(table);
        });
    }

    function scheduleRefresh() {
        if (refreshFrame !== null) return;
        refreshFrame = window.requestAnimationFrame(refresh);
    }

    const mutationObserver = new MutationObserver(scheduleRefresh);
    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });

    resizeObserver.observe(document.documentElement);
    scheduleRefresh();

    return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        if (refreshFrame !== null) window.cancelAnimationFrame(refreshFrame);
    };
}
